/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Run } from "../shared/types";
import { StepAnswer, type AnswerInput } from "./StepAnswer";

type Props = {
  /** Open (or create) the run of this procedure for this day… */
  procedureId?: number;
  date?: string;
  /** …or an existing run (history). */
  runId?: number;
  onClose: (changed: boolean) => void;
};

// Work through a procedure: every answer saves at once (several people can
// share a run); sign off when the required steps are done.
export const ProcedureRunner = defineComponent<Props>({
  name: "ProcedureRunner",
  props: ["procedureId", "date", "runId", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const run = ref<Run | null>(null);
    const saving = ref(false);
    const changed = ref(false);
    const notes = ref("");

    onMounted(async () => {
      try {
        run.value = (props.runId
          ? await trpc.workday.procedures.run.query({ id: props.runId })
          : await trpc.workday.procedures.openRun.mutate({ procedure_id: props.procedureId!, date: props.date! })) as Run;
        notes.value = run.value.notes ?? "";
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
        props.onClose(false);
      }
    });

    const readOnly = computed(() => run.value?.status === "completed");
    const isDone = (s: Run["steps"][number]) => s.answer != null && (s.kind !== "check" || s.answer.checked);
    const progress = computed(() => {
      const steps = run.value?.steps ?? [];
      return { done: steps.filter(isDone).length, total: steps.length };
    });
    const missingRequired = computed(() => (run.value?.steps ?? []).filter((s) => s.required && !isDone(s)).length);

    const answer = async (stepId: number, a: AnswerInput) => {
      if (!run.value) return;
      saving.value = true;
      try {
        run.value = (await trpc.workday.procedures.answer.mutate({ run_id: run.value.id, step_id: stepId, ...a })) as Run;
        changed.value = true;
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const complete = async () => {
      if (!run.value) return;
      saving.value = true;
      try {
        run.value = (await trpc.workday.procedures.complete.mutate({ run_id: run.value.id, notes: notes.value || null })) as Run;
        toast.showToast(t("workday.runner.completed"), { type: "success" });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const renderHeader = (r: Run) => {
      const pct = progress.value.total ? Math.round((progress.value.done / progress.value.total) * 100) : 0;
      return (
        <div class="space-y-2">
          <div class="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span class="text-base-content/60">
              {t(`workday.department.${r.department}`)} · {new Date(`${r.run_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </span>
            {r.issues > 0 && (
              <span class="badge badge-error gap-1">
                <span class="material-symbols-outlined text-[14px]">warning</span>
                {t("workday.common.issues").replace("{count}", String(r.issues))}
              </span>
            )}
          </div>
          <div class="flex items-center gap-3">
            <progress class={["progress h-2 flex-1", r.status === "completed" ? "progress-success" : "progress-primary"].join(" ")} value={pct} max="100" />
            <span class="text-sm font-medium tabular-nums">{`${progress.value.done}/${progress.value.total}`}</span>
          </div>
          {r.status === "completed" && (
            <div class="alert alert-success py-2 text-sm">
              <span class="material-symbols-outlined">verified</span>
              {t("workday.runner.signed_off")
                .replace("{name}", r.completed_by_name ?? "—")
                .replace("{time}", r.completed_at ? new Date(r.completed_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "")}
            </div>
          )}
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={run.value?.procedure_name ?? t("workday.procedures.title")}
        onClose={() => props.onClose(changed.value)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex items-center justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose(changed.value)}>
                {readOnly.value ? t("workday.common.close") : t("workday.runner.save_later")}
              </button>
              {!readOnly.value && (
                <div class="flex items-center gap-3">
                  {missingRequired.value > 0 && (
                    <span class="hidden text-xs text-base-content/60 sm:inline">
                      {t("workday.runner.remaining").replace("{count}", String(missingRequired.value))}
                    </span>
                  )}
                  <button class="btn btn-primary" disabled={saving.value || missingRequired.value > 0} onClick={complete}>
                    <span class="material-symbols-outlined text-base">verified</span>
                    {t("workday.runner.sign_off")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      >
        {!run.value ? (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        ) : (
          <div class="space-y-4 p-6">
            {renderHeader(run.value)}
            <div class="space-y-2">
              {run.value.steps.map((s, i) => (
                <StepAnswer
                  key={s.id}
                  step={s}
                  index={i}
                  readOnly={readOnly.value}
                  saving={saving.value}
                  onAnswer={(a: AnswerInput) => answer(s.id, a)}
                />
              ))}
            </div>
            {(!readOnly.value || notes.value) && (
              <label class="block">
                <span class="mb-1 block text-sm font-medium">{t("workday.runner.notes")}</span>
                <textarea class="textarea textarea-bordered w-full text-sm" rows={2} disabled={readOnly.value} v-model={notes.value} placeholder={t("workday.runner.notes_placeholder")} />
              </label>
            )}
          </div>
        )}
      </LemmonModal>
    );
  },
});
